import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldCheck } from "lucide-react";
import { assocLogin, assocForgot, assocReset, partnerMfaSetupBegin, partnerMfaSetupVerify, partnerMfaVerify, formatApiError } from "@/lib/api";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Sections5";

const inp = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-saffron";
const lab = "block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1";

export default function PartnerAuthPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const resetToken = params.get("token");
    const [mode, setMode] = useState(resetToken ? "reset" : "login"); // login | forgot | reset
    const [form, setForm] = useState({ identifier: "", password: "", email: "", confirm: "" });
    const [busy, setBusy] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);
    // 2FA stages: login → setup (first login, QR enrolment) / mfa (returning) → backup (codes shown once)
    const [stage, setStage] = useState("login");
    const [mfaToken, setMfaToken] = useState("");
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState("");
    const [mfaError, setMfaError] = useState("");
    const [backupCodes, setBackupCodes] = useState(null);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const finishLogin = (res) => {
        localStorage.setItem("gc_partner_token", res.token);
        localStorage.setItem("gc_partner", JSON.stringify(res.associate));
    };

    const beginSetup = async (mToken) => {
        const s = await partnerMfaSetupBegin(mToken);
        setSetup(s);
        setStage("setup");
    };

    const doLogin = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await assocLogin({ identifier: form.identifier.trim(), password: form.password });
            if (res.mfa_setup_required || res.mfa_required) {
                setMfaToken(res.mfa_token);
                setMfaError("");
                setCode("");
                if (res.mfa_setup_required) await beginSetup(res.mfa_token);
                else setStage("mfa");
                return;
            }
            finishLogin(res);
            toast.success(`Welcome back, ${res.associate.name.split(" ")[0]}!`);
            navigate("/partner");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally { setBusy(false); }
    };

    const submitCode = async (e) => {
        e.preventDefault();
        setBusy(true);
        setMfaError("");
        try {
            const res = stage === "setup" ? await partnerMfaSetupVerify(mfaToken, code) : await partnerMfaVerify(mfaToken, code);
            finishLogin(res);
            if (res.backup_codes) {
                setBackupCodes(res.backup_codes);
                setStage("backup");
            } else {
                toast.success("Verified — welcome back!");
                navigate("/partner");
            }
        } catch (err) {
            setMfaError(formatApiError(err.response?.data?.detail));
        } finally { setBusy(false); }
    };

    const doForgot = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await assocForgot(form.email.trim());
            setForgotSent(true);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally { setBusy(false); }
    };

    const doReset = async (e) => {
        e.preventDefault();
        if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        if (form.password !== form.confirm) { toast.error("Passwords don't match"); return; }
        setBusy(true);
        try {
            await assocReset({ token: resetToken, password: form.password });
            toast.success("Password set — log in now");
            navigate("/partner/login");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-cream" data-testid="partner-auth-page">
            <Header />
            <div className="flex-1 flex items-center justify-center px-6 py-12">
            <div className="w-full max-w-md rounded-2xl bg-white border border-stone-200 p-7 shadow-sm">
                <div className="flex items-center gap-2 text-saffron"><KeyRound className="h-5 w-5" />
                    <p className="text-[11px] font-black uppercase tracking-wider">Partners Portal</p></div>

                {stage === "backup" && backupCodes && (
                    <div data-testid="partner-mfa-backup">
                        <div className="flex items-center gap-2 mt-2"><ShieldCheck className="h-5 w-5 text-green-600" />
                            <h1 className="font-display font-black text-2xl text-navy">Authenticator linked</h1></div>
                        <p className="mt-2 text-sm text-stone-600">Save these one-time recovery codes somewhere safe. Each works once if you lose your phone.</p>
                        <div className="mt-4 grid grid-cols-2 gap-2 rounded-xl bg-stone-50 border border-stone-200 p-4">
                            {backupCodes.map((c) => <code key={c} className="text-center text-sm font-bold text-navy tracking-widest" data-testid="partner-backup-code">{c}</code>)}
                        </div>
                        <button onClick={() => navigate("/partner")} data-testid="partner-mfa-finish"
                            className="mt-5 w-full rounded-full bg-saffron py-3 text-sm font-display font-black uppercase tracking-wider text-white hover:bg-saffron-dark">
                            I've saved them — continue
                        </button>
                    </div>
                )}

                {(stage === "setup" || stage === "mfa") && (
                    <form onSubmit={submitCode} className="mt-2" data-testid="partner-mfa-form">
                        <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-saffron" />
                            <h1 className="font-display font-black text-2xl text-navy">Two-factor check</h1></div>
                        {stage === "setup" ? (
                            <>
                                <p className="mt-2 text-sm text-stone-600">Partners accounts are protected with an authenticator app. Scan this with Google Authenticator / Authy, then enter the 6-digit code.</p>
                                {setup && (
                                    <div className="mt-4 rounded-xl bg-stone-50 border border-stone-200 p-4 text-center">
                                        <img src={setup.qr} alt="Authenticator QR code" className="mx-auto h-40 w-40" data-testid="partner-mfa-qr" />
                                        <p className="mt-2 text-[10px] uppercase tracking-wider text-stone-400 font-bold">Can't scan? Enter this key manually</p>
                                        <code className="text-xs font-bold text-navy tracking-widest break-all" data-testid="partner-mfa-secret">{setup.secret}</code>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="mt-2 text-sm text-stone-600">Enter the 6-digit code from your authenticator app, or a recovery code.</p>
                        )}
                        {mfaError && <div className="mt-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="partner-mfa-error">{mfaError}</div>}
                        <div className="mt-4"><label className={lab}>Authentication code</label>
                            <input inputMode="numeric" autoComplete="one-time-code" data-testid="partner-mfa-code"
                                className={`${inp} text-center text-lg font-bold tracking-[0.4em]`}
                                value={code} onChange={(e) => setCode(e.target.value.trim())} required /></div>
                        <button type="submit" disabled={busy} data-testid="partner-mfa-submit"
                            className="mt-5 w-full rounded-full bg-saffron py-3 text-sm font-display font-black uppercase tracking-wider text-white hover:bg-saffron-dark disabled:opacity-60 flex items-center justify-center gap-2">
                            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify & continue
                        </button>
                        <button type="button" onClick={() => { setStage("login"); setCode(""); setMfaError(""); }} data-testid="partner-mfa-back"
                            className="mt-3 w-full text-xs font-bold text-stone-400 hover:text-navy">← Back to login</button>
                    </form>
                )}

                {stage === "login" && mode === "login" && (
                    <>
                        <h1 className="mt-2 font-display font-black text-2xl text-navy">Partner login</h1>
                        <form onSubmit={doLogin} className="mt-5 space-y-4" data-testid="partner-login-form">
                            <div><label className={lab}>Email or Associate ID</label>
                                <input className={inp} value={form.identifier} onChange={set("identifier")} data-testid="login-identifier" placeholder="you@example.com or GCE-XXXXXX" /></div>
                            <div><label className={lab}>Password</label>
                                <input className={inp} type="password" value={form.password} onChange={set("password")} data-testid="login-password" placeholder="Your password" /></div>
                            <button type="submit" disabled={busy} data-testid="login-submit"
                                className="w-full rounded-full bg-saffron py-3 text-sm font-display font-black uppercase tracking-wider text-white hover:bg-saffron-dark disabled:opacity-60 flex items-center justify-center gap-2">
                                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Log in
                            </button>
                        </form>
                        <div className="mt-4 flex justify-between text-xs">
                            <button onClick={() => setMode("forgot")} className="font-bold text-navy hover:text-saffron" data-testid="forgot-link">Forgot password?</button>
                            <Link to="/partners" className="text-stone-500 hover:text-navy">New here? See roles</Link>
                        </div>
                    </>
                )}

                {stage === "login" && mode === "forgot" && (
                    <>
                        <h1 className="mt-2 font-display font-black text-2xl text-navy">Reset password</h1>
                        {forgotSent ? (
                            <p className="mt-4 text-sm text-stone-600" data-testid="forgot-sent">If an account exists for this email, a reset link is on its way. Check your inbox (and spam).</p>
                        ) : (
                            <form onSubmit={doForgot} className="mt-5 space-y-4" data-testid="partner-forgot-form">
                                <div><label className={lab}>Account email</label>
                                    <input className={inp} type="email" value={form.email} onChange={set("email")} data-testid="forgot-email" placeholder="you@example.com" /></div>
                                <button type="submit" disabled={busy} data-testid="forgot-submit"
                                    className="w-full rounded-full bg-navy py-3 text-sm font-display font-black uppercase tracking-wider text-white hover:bg-navy-light disabled:opacity-60 flex items-center justify-center gap-2">
                                    {busy && <Loader2 className="h-4 w-4 animate-spin" />} Email me a reset link
                                </button>
                            </form>
                        )}
                        <button onClick={() => setMode("login")} className="mt-4 text-xs font-bold text-navy hover:text-saffron" data-testid="back-to-login">← Back to login</button>
                    </>
                )}

                {stage === "login" && mode === "reset" && (
                    <>
                        <h1 className="mt-2 font-display font-black text-2xl text-navy">Set your password</h1>
                        <form onSubmit={doReset} className="mt-5 space-y-4" data-testid="partner-reset-form">
                            <div><label className={lab}>New password (min 8 characters)</label>
                                <input className={inp} type="password" value={form.password} onChange={set("password")} data-testid="reset-password" /></div>
                            <div><label className={lab}>Confirm password</label>
                                <input className={inp} type="password" value={form.confirm} onChange={set("confirm")} data-testid="reset-confirm" /></div>
                            <button type="submit" disabled={busy} data-testid="reset-submit"
                                className="w-full rounded-full bg-saffron py-3 text-sm font-display font-black uppercase tracking-wider text-white hover:bg-saffron-dark disabled:opacity-60 flex items-center justify-center gap-2">
                                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Set password
                            </button>
                        </form>
                    </>
                )}
            </div>
            </div>
            <Footer />
        </div>
    );
}
